import {
  ArrowLeft,
  CheckCircle2,
  CreditCard,
  Gamepad2,
  LockKeyhole,
  ShieldCheck,
  ShoppingBag,
  Store as StoreIcon,
} from 'lucide-react'

import { cn } from '@/lib/cn'

import { DISCORD, PLAY, type Package } from './api'
import { packagesOf, useSections, useShelves } from './catalogue'
import { safeHtml } from './html'
import { useStore } from './state'
import { categoryPath } from './router'
import { BuyButtons, Eyebrow, Link, PackageCard, Picture, Price, SectionTitle } from './ui'

function Grid({ packages }: { packages: Package[] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {packages.map((pkg, index) => (
        <PackageCard key={pkg.id} pkg={pkg} index={index} />
      ))}
    </div>
  )
}

function SkeletonGrid() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-hidden>
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="card overflow-hidden">
          <div className="aspect-[16/10] animate-pulse bg-white/[0.03]" />
          <div className="space-y-3 p-5">
            <div className="h-4 w-2/3 animate-pulse rounded bg-white/[0.06]" />
            <div className="h-3 w-full animate-pulse rounded bg-white/[0.04]" />
            <div className="h-9 w-24 animate-pulse rounded-full bg-white/[0.05]" />
          </div>
        </div>
      ))}
    </div>
  )
}

function Steps() {
  const steps = [
    { icon: ShoppingBag, title: 'Scegli', text: 'Metti nel carrello quello che ti serve.' },
    {
      icon: Gamepad2,
      title: 'Accedi con FiveM',
      text: 'Il tuo account Cfx.re dice a chi consegnare.',
    },
    {
      icon: LockKeyhole,
      title: 'Paga in sicurezza',
      text: 'Carta, PayPal e altri metodi, tramite Tebex.',
    },
  ]
  return (
    <ol className="mt-10 grid gap-3 sm:grid-cols-3">
      {steps.map(({ icon: Icon, title, text }, index) => (
        <li
          key={title}
          className="glass animate-rise flex items-start gap-3.5 rounded-2xl p-4"
          style={{ animationDelay: `${540 + index * 90}ms` }}
        >
          <span className="bg-blue/15 text-glow ring-glow/20 grid size-9 shrink-0 place-items-center rounded-xl ring-1">
            <Icon className="size-[18px]" aria-hidden />
          </span>
          <span>
            <span className="text-mist block text-[11px] font-semibold tracking-[0.2em] uppercase">
              Passo {index + 1}
            </span>
            <span className="mt-0.5 block text-[14px] font-semibold">{title}</span>
            <span className="text-mist mt-0.5 block text-[13px] leading-relaxed">{text}</span>
          </span>
        </li>
      ))}
    </ol>
  )
}

export function HomePage() {
  const filled = useSections()
  const failed = useStore((state) => state.failed)

  return (
    <>
      <section className="relative isolate pt-8 pb-12 sm:pt-16 sm:pb-16">
        <div className="hero-backdrop" aria-hidden>
          <div className="aurora" />
          <div className="beams" />
        </div>
        <Eyebrow className="animate-rise">Store ufficiale</Eyebrow>
        <h1 className="font-display mt-5 max-w-3xl text-[clamp(2.1rem,6vw,4rem)] leading-[1.03] font-semibold tracking-[-0.02em] text-balance">
          {['Sostieni', 'la', 'città.'].map((word, index) => (
            <span
              key={word}
              className="text-lit animate-word inline-block pr-[0.25em]"
              style={{ animationDelay: `${120 + index * 110}ms` }}
            >
              {word}
            </span>
          ))}
        </h1>
        <p
          className="text-fog/90 animate-rise mt-5 max-w-2xl text-[clamp(1rem,1.4vw,1.1rem)] leading-relaxed text-pretty"
          style={{ animationDelay: '420ms' }}
        >
          Ogni acquisto tiene accesi i server di Unity RP e finanzia quello che costruiamo dopo. I
          pacchetti arrivano in gioco sul tuo account FiveM, di solito entro pochi minuti.
        </p>
        <Steps />
      </section>

      {filled === undefined && <SkeletonGrid />}
      {filled?.length === 0 && (
        <Empty
          title={failed ? 'Il catalogo non risponde' : 'Ancora niente in vetrina'}
          text={
            failed
              ? 'Tebex non ha risposto. Riprova tra qualche istante.'
              : 'I primi pacchetti arrivano presto: tieni d’occhio il Discord.'
          }
        />
      )}
      <div className="space-y-16">
        {filled?.map(({ category, children }) => (
          <section key={category.id} aria-labelledby={`c${category.id}`}>
            <SectionTitle
              aside={
                <Link
                  to={categoryPath(category)}
                  className="text-glow text-[13px] font-medium hover:text-white"
                >
                  Apri la categoria
                </Link>
              }
            >
              <span id={`c${category.id}`}>{category.name}</span>
            </SectionTitle>
            {packagesOf(category).length > 0 && <Grid packages={packagesOf(category)} />}
            {children
              .filter((child) => packagesOf(child).length > 0)
              .map((child) => (
                <div key={child.id} className="mt-8">
                  <h3 className="text-fog mb-4 text-[14px] font-semibold">{child.name}</h3>
                  <Grid packages={packagesOf(child)} />
                </div>
              ))}
          </section>
        ))}
      </div>
    </>
  )
}

function Back() {
  return (
    <Link
      to="/"
      className="text-mist mb-6 inline-flex items-center gap-1.5 text-[13px] font-medium hover:text-white"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Tutto lo store
    </Link>
  )
}

export function CategoryPage({ id }: { id: number }) {
  const shelves = useShelves()
  const categories = useStore((state) => state.categories)
  if (!categories) return <SkeletonGrid />
  const category = categories.find((each) => each.id === id)
  if (!category) return <NotFoundPage />
  const children = shelves?.find((shelf) => shelf.category.id === id)?.children ?? []

  return (
    <div className="pt-4">
      <Back />
      {category.parent && <Eyebrow>{category.parent.name}</Eyebrow>}
      <h1 className="font-display mt-3 text-[clamp(1.8rem,4.5vw,2.8rem)] leading-[1.08] font-semibold tracking-[-0.02em]">
        <span className="text-lit">{category.name}</span>
      </h1>
      {category.description && (
        <div
          className="prose-store mt-4 max-w-3xl"
          dangerouslySetInnerHTML={{ __html: safeHtml(category.description) }}
        />
      )}
      <div className="mt-10 space-y-12">
        {packagesOf(category).length > 0 && <Grid packages={packagesOf(category)} />}
        {children.map((child) => (
          <section key={child.id}>
            <SectionTitle>{child.name}</SectionTitle>
            <Grid packages={packagesOf(child)} />
          </section>
        ))}
        {packagesOf(category).length === 0 && children.length === 0 && (
          <Empty title="Niente qui, per ora" text="Questa categoria è ancora vuota." />
        )}
      </div>
    </div>
  )
}

export function PackagePage({ id }: { id: number }) {
  const categories = useStore((state) => state.categories)
  if (!categories) return <SkeletonGrid />
  const pkg = categories
    .flatMap((category) => category.packages ?? [])
    .find((each) => each.id === id)
  if (!pkg) return <NotFoundPage />
  const category = categories.find((each) => each.id === pkg.category.id)
  const gallery = (pkg.media ?? []).filter(
    (media) => media.type === 'image' && media.url !== pkg.image,
  )

  return (
    <div className="pt-4">
      <Back />
      <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:gap-12">
        <div>
          <Picture pkg={pkg} className="card aspect-[16/10] rounded-3xl" />
          {gallery.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-3">
              {gallery.slice(0, 6).map((media) => (
                <a
                  key={media.url}
                  href={media.url}
                  target="_blank"
                  rel="noreferrer"
                  className="card block aspect-[16/10] overflow-hidden rounded-xl"
                >
                  <img
                    src={media.url}
                    alt={media.name ?? ''}
                    loading="lazy"
                    className="size-full object-cover"
                  />
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="lg:pt-2">
          {category && (
            <Link to={categoryPath(category)} className="hover:opacity-80">
              <Eyebrow>{category.name}</Eyebrow>
            </Link>
          )}
          <h1 className="font-display mt-4 text-[clamp(1.7rem,4vw,2.6rem)] leading-[1.08] font-semibold tracking-[-0.02em] text-balance">
            {pkg.name}
          </h1>
          <div className="mt-5">
            <Price pkg={pkg} large />
          </div>
          <div className="mt-6">
            <BuyButtons pkg={pkg} large />
          </div>
          <ul className="text-mist mt-6 space-y-2 text-[13px]">
            <li className="flex items-center gap-2">
              <Gamepad2 className="text-glow size-4" aria-hidden />
              Consegnato in gioco sul tuo account FiveM
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="text-glow size-4" aria-hidden />
              Pagamento sicuro gestito da Tebex
            </li>
          </ul>
          {pkg.description && (
            <div
              className="prose-store mt-8 border-t border-white/[0.07] pt-8"
              dangerouslySetInnerHTML={{ __html: safeHtml(pkg.description) }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export function ThanksPage() {
  return (
    <div className="mx-auto max-w-xl py-16 text-center sm:py-24">
      <span className="bg-live/10 text-live ring-live/25 mx-auto grid size-16 place-items-center rounded-full ring-1">
        <CheckCircle2 className="size-8" aria-hidden />
      </span>
      <h1 className="font-display mt-7 text-[clamp(1.8rem,5vw,2.6rem)] leading-tight font-semibold tracking-[-0.02em]">
        <span className="text-lit">Grazie di cuore.</span>
      </h1>
      <p className="text-fog mt-4 text-[15px] leading-relaxed">
        Il pagamento è in elaborazione. I pacchetti arrivano in gioco entro pochi minuti; se non sei
        in città, li trovi la prossima volta che entri. La ricevuta ti arriva per email da Tebex.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-3">
        <a
          href={PLAY}
          className="btn-primary inline-flex h-12 items-center gap-2 rounded-full px-6 text-[15px] font-semibold"
        >
          <Gamepad2 className="size-4" aria-hidden />
          Entra in città
        </a>
        <Link
          to="/"
          className="glass inline-flex h-12 items-center gap-2 rounded-full px-6 text-[15px] font-medium transition hover:bg-white/10"
        >
          Torna allo store
        </Link>
      </div>
      <p className="text-mist mt-8 text-[13px]">
        Non è arrivato niente dopo un’ora? Apri un ticket sul{' '}
        <a href={DISCORD} className="text-glow hover:text-white">
          Discord
        </a>
        .
      </p>
    </div>
  )
}

function Empty({ title, text, className }: { title: string; text: string; className?: string }) {
  return (
    <div className={cn('card px-6 py-14 text-center', className)}>
      <StoreIcon className="text-mist mx-auto size-7" aria-hidden />
      <p className="font-display mt-4 text-[17px] font-semibold">{title}</p>
      <p className="text-mist mt-2 text-[14px]">{text}</p>
    </div>
  )
}

export function NotFoundPage() {
  return (
    <div className="py-16">
      <Empty
        title="Qui non c’è niente"
        text="Il pacchetto o la pagina che cerchi non esiste più."
      />
      <div className="mt-6 text-center">
        <Link to="/" className="text-glow text-[14px] font-medium hover:text-white">
          Torna allo store
        </Link>
      </div>
    </div>
  )
}

export function ClosedPage({ failed }: { failed: boolean }) {
  return (
    <div className="mx-auto max-w-xl py-20 text-center">
      <span className="glass mx-auto grid size-16 place-items-center rounded-full">
        <CreditCard className="text-glow size-7" aria-hidden />
      </span>
      <h1 className="font-display mt-7 text-[clamp(1.8rem,5vw,2.4rem)] font-semibold tracking-[-0.02em]">
        <span className="text-lit">
          {failed ? 'Lo store non risponde' : 'Lo store riapre presto'}
        </span>
      </h1>
      <p className="text-fog mt-4 text-[15px] leading-relaxed">
        {failed
          ? 'Riprova tra qualche istante. Se continua, controlla lo stato dei servizi.'
          : 'Stiamo sistemando le vetrine. Le novità arrivano prima sul Discord.'}
      </p>
      <a
        href={DISCORD}
        className="btn-primary mt-8 inline-flex h-12 items-center gap-2 rounded-full px-6 text-[15px] font-semibold"
      >
        Vai al Discord
      </a>
    </div>
  )
}
